import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  Sms as SmsIcon,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import {
  selectLoginLoading,
  selectLoginError,
  selectTwoFactorMethods,
  selectTwoFactorSelectedMethod,
  selectTwoFactorMaskedContact,
  selectTwoFactorCodeSent,
  selectTwoFactorResendCountdown,
  selectTwoFactorAutoSendTriggered,
  sendTwoFactorCodeRequest,
  verifyTwoFactorCodeRequest,
  resendTwoFactorCodeRequest,
  updateResendCountdown,
  cancelTwoFactor,
  clearError,
  setAutoSendTriggered,
  selectTwoFactorMethod,
} from './slice';

// Method constants matching backend: 1=SMS, 2=Email
const METHOD_SMS = 1;
const METHOD_EMAIL = 2;

function TwoFactorVerification() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const loading = useSelector(selectLoginLoading);
  const error = useSelector(selectLoginError);
  const methods = useSelector(selectTwoFactorMethods); // Array of { method, key, label, available, maskedContact }
  const selectedMethod = useSelector(selectTwoFactorSelectedMethod);
  const maskedContact = useSelector(selectTwoFactorMaskedContact);
  const codeSent = useSelector(selectTwoFactorCodeSent);
  const resendCountdown = useSelector(selectTwoFactorResendCountdown);
  const autoSendTriggered = useSelector(selectTwoFactorAutoSendTriggered);

  const [verificationCode, setVerificationCode] = useState('');
  const [localSelectedMethod, setLocalSelectedMethod] = useState(null);
  const codeInputRef = useRef(null);

  // Get available methods only
  const availableMethods = methods.filter((m) => m.available !== false);

  // Auto-send OTP if only one method available
  useEffect(() => {
    if (!autoSendTriggered && availableMethods.length === 1 && !codeSent && !loading) {
      const singleMethod = availableMethods[0];
      console.log('[2FA] Auto-sending OTP for single method:', singleMethod);
      dispatch(setAutoSendTriggered());
      dispatch(selectTwoFactorMethod(singleMethod.method));
      setLocalSelectedMethod(singleMethod);
      dispatch(sendTwoFactorCodeRequest({ method: singleMethod.method }));
    }
  }, [availableMethods, autoSendTriggered, codeSent, loading, dispatch]);

  // Focus on code input when code is sent
  useEffect(() => {
    if (codeSent && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [codeSent]);

  // Countdown timer for resend button
  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setInterval(() => {
        dispatch(updateResendCountdown(resendCountdown - 1));
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendCountdown, dispatch]);

  const handleSelectMethod = (methodObj) => {
    setLocalSelectedMethod(methodObj);
    dispatch(selectTwoFactorMethod(methodObj.method));
    dispatch(clearError());
  };

  const handleSendCode = () => {
    if (localSelectedMethod) {
      dispatch(sendTwoFactorCodeRequest({ method: localSelectedMethod.method }));
    }
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();
    if (verificationCode.length >= 6) {
      dispatch(verifyTwoFactorCodeRequest({ code: verificationCode }));
    }
  };

  const handleResendCode = () => {
    if (resendCountdown === 0) {
      const method = localSelectedMethod?.method || selectedMethod;
      dispatch(resendTwoFactorCodeRequest({ method }));
    }
  };

  const handleCancel = () => {
    dispatch(cancelTwoFactor());
  };

  const handleCodeChange = (e) => {
    // Only allow digits, max 6 characters
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
    if (error) {
      dispatch(clearError());
    }
  };

  const getMethodIcon = (methodNum) => {
    return methodNum === METHOD_SMS ? <SmsIcon /> : <EmailIcon />;
  };

  const getMethodLabel = (methodObj) => {
    // Use label from BE if available, otherwise use translation
    if (methodObj.label) return methodObj.label;
    return methodObj.method === METHOD_SMS
      ? t('login.twoFactor.methodSms')
      : t('login.twoFactor.methodEmail');
  };

  const getCurrentMaskedContact = () => {
    // Priority: maskedContact from state > maskedContact from selected method
    if (maskedContact) return maskedContact;
    if (localSelectedMethod?.maskedContact) return localSelectedMethod.maskedContact;
    return null;
  };

  // Show loading while auto-sending
  if (availableMethods.length === 1 && !codeSent && (loading || !autoSendTriggered)) {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <CircularProgress size={48} sx={{ color: 'white', mb: 2 }} />
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {t('login.twoFactor.sendingCode')}
        </Typography>
      </Box>
    );
  }

  // Step 1: Select 2FA method (only show if multiple methods available)
  if (!codeSent && availableMethods.length > 1) {
    return (
      <Box sx={{ width: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '30px',
          }}
        >
          <SecurityIcon
            sx={{
              fontSize: 48,
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '16px',
            }}
          />
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '8px',
            }}
          >
            {t('login.twoFactor.title')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              textAlign: 'center',
            }}
          >
            {t('login.twoFactor.selectMethodDescription')}
          </Typography>
        </Box>

        {/* Method Selection */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: '24px' }}>
          {availableMethods.map((methodObj) => (
            <Button
              key={methodObj.method}
              variant={localSelectedMethod?.method === methodObj.method ? 'contained' : 'outlined'}
              onClick={() => handleSelectMethod(methodObj)}
              startIcon={getMethodIcon(methodObj.method)}
              disabled={loading}
              id={`method-${methodObj.method}`}
              name={`method-${methodObj.method}`}
              data-testid={`login-2fa-button-method-${methodObj.method}`}
              sx={{
                padding: '14px 20px',
                justifyContent: 'flex-start',
                color: localSelectedMethod?.method === methodObj.method ? '#2d8659' : 'white',
                borderColor: localSelectedMethod?.method === methodObj.method ? 'white' : 'rgba(255, 255, 255, 0.5)',
                backgroundColor: localSelectedMethod?.method === methodObj.method ? 'white' : 'transparent',
                textTransform: 'none',
                fontSize: '16px',
                '&:hover': {
                  backgroundColor: localSelectedMethod?.method === methodObj.method ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'white',
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{getMethodLabel(methodObj)}</span>
                {methodObj.maskedContact && (
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {methodObj.maskedContact}
                  </Typography>
                )}
              </Box>
            </Button>
          ))}
        </Box>

        {/* Error */}
        {error && (
          <Alert
            severity="error"
            sx={{
              marginBottom: '16px',
              backgroundColor: 'rgba(255, 0, 0, 0.15)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '& .MuiAlert-icon': {
                color: 'white',
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="text"
            onClick={handleCancel}
            startIcon={<ArrowBackIcon />}
            disabled={loading}
            id="cancelTwoFactor"
            name="cancelTwoFactor"
            data-testid="login-2fa-button-cancel"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              textTransform: 'none',
              '&:hover': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {t('login.twoFactor.back')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleSendCode}
            disabled={!localSelectedMethod || loading}
            id="sendCode"
            name="sendCode"
            data-testid="login-2fa-button-sendCode"
            sx={{
              flex: 1,
              padding: '10px 24px',
              color: 'white',
              borderColor: 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'white',
              },
              '&:disabled': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ color: 'white', marginRight: 1 }} />
                {t('login.twoFactor.sending')}
              </>
            ) : (
              t('login.twoFactor.sendCode')
            )}
          </Button>
        </Box>
      </Box>
    );
  }

  // Step 2: Enter verification code
  const currentMaskedContact = getCurrentMaskedContact();
  const currentMethodNum = localSelectedMethod?.method || selectedMethod;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '30px',
        }}
      >
        <SecurityIcon
          sx={{
            fontSize: 48,
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '16px',
          }}
        />
        <Typography
          variant="h5"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: '8px',
          }}
        >
          {t('login.twoFactor.enterCodeTitle')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            textAlign: 'center',
          }}
        >
          {currentMaskedContact
            ? t('login.twoFactor.codeSentTo', { contact: currentMaskedContact })
            : currentMethodNum === METHOD_SMS
              ? t('login.twoFactor.codeSentViaSms')
              : t('login.twoFactor.codeSentViaEmail')}
        </Typography>
      </Box>

      {/* Verification Code Form */}
      <Box component="form" onSubmit={handleVerifyCode} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          type="text"
          value={verificationCode}
          onChange={handleCodeChange}
          placeholder={t('login.twoFactor.codePlaceholder')}
          disabled={loading}
          autoComplete="one-time-code"
          fullWidth
          variant="outlined"
          inputRef={codeInputRef}
          inputProps={{
            id: 'verificationCode',
            name: 'verificationCode',
            'data-testid': 'login-2fa-input-code',
            maxLength: 6,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            style: {
              textAlign: 'center',
              letterSpacing: '12px',
              fontSize: '24px',
              fontWeight: 'bold',
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              color: 'white',
              fontSize: '24px',
              borderRadius: '8px',
              '& fieldset': {
                border: '2px solid rgba(255, 255, 255, 0.3)',
              },
              '&:hover fieldset': {
                border: '2px solid rgba(255, 255, 255, 0.5)',
              },
              '&.Mui-focused fieldset': {
                border: '2px solid rgba(255, 255, 255, 0.8)',
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '16px',
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1,
                textAlign: 'center',
                letterSpacing: 'normal',
                fontSize: '16px',
                fontWeight: 'normal',
              },
            },
          }}
        />

        {/* Error */}
        {error && (
          <Alert
            severity="error"
            sx={{
              backgroundColor: 'rgba(255, 0, 0, 0.15)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '& .MuiAlert-icon': {
                color: 'white',
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Resend Code */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '8px',
            }}
          >
            {t('login.twoFactor.didNotReceive')}
          </Typography>
          <Button
            variant="text"
            onClick={handleResendCode}
            disabled={resendCountdown > 0 || loading}
            id="resendCode"
            name="resendCode"
            data-testid="login-2fa-button-resend"
            sx={{
              color: resendCountdown > 0 ? 'rgba(255, 255, 255, 0.4)' : 'white',
              textTransform: 'none',
              textDecoration: 'underline',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            {resendCountdown > 0
              ? t('login.twoFactor.resendIn', { seconds: resendCountdown })
              : t('login.twoFactor.resendCode')}
          </Button>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, marginTop: '8px' }}>
          <Button
            variant="text"
            onClick={handleCancel}
            startIcon={<ArrowBackIcon />}
            disabled={loading}
            id="backToLogin"
            name="backToLogin"
            data-testid="login-2fa-button-back"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              textTransform: 'none',
              '&:hover': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {t('login.twoFactor.back')}
          </Button>
          <Button
            type="submit"
            variant="outlined"
            disabled={verificationCode.length < 6 || loading}
            id="verifyCode"
            name="verifyCode"
            data-testid="login-2fa-button-verify"
            sx={{
              flex: 1,
              padding: '10px 24px',
              color: 'white',
              borderColor: 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'white',
              },
              '&:disabled': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ color: 'white', marginRight: 1 }} />
                {t('login.twoFactor.verifying')}
              </>
            ) : (
              t('login.twoFactor.verify')
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default TwoFactorVerification;
